<template>
  <div class="comp-fh-board-view" :style="{ height: _height + 10 + 'px' }" ref="scrollContainer" @scroll="handleScroll">
    <div v-if="visibleGroups.length" class="gr-wrapper">
      <div class="gr-spacer" :style="{ width: totalWidth + 'px', height: '100%' }"></div>
      <div class="gr-content" :style="{ transform: `translateX(${startOffset}px)` }">
        <div v-for="(group, index) in visibleGroups" :key="group.groupKey" class="card-group" :style="{ background: getDefaultColor(group, index)?.background + (getDefaultColor(group, index)?.color == '#fff' ? '20' : '60'), width: itemWidth + 'px' }">
          <div class="card-group-title">
            <jw-tag :color="getDefaultColor(group, index)?.background" :size="'small'">
              <div class="title" :style="{ color: getDefaultColor(group, index)?.color }">
                {{ group?.business?.userName || group.name }}
              </div>
            </jw-tag>
            <div class="number">{{ group?.count }}</div>
          </div>
          <fh-board-card v-if="group.data?.length" :entityId="entityId" :viewId="viewId" @scrollBottom="() => handleScrollBottom(group)" @getComputedStatus="getComputedStatus" :columns="allColumns" :records="group.data" :groupsItemData="[]" :height="_height" @toDetails="toDetails" :businessData="_businessData" :shortcutBtnPermission="shortcutBtnPermission" @shortcutBtnCb="refreshList" />
        </div>
      </div>
    </div>
    <div v-else-if="loading" class="loading">
      <fh-list-skeleton :rows="10" :columns="6" />
    </div>
    <div v-else-if="allGroups?.length == 0" class="no-data">
      <jw-empty :img-src="groupConfigErrorSvg" :description="t('无符合条件的数据')"></jw-empty>
    </div>
  </div>
  <dashboard-field-setting @cb="refreshList" :viewId="viewId" :entity="entityId" :businessData="_businessData" :fields="allColumns" ref="dashboardFieldRef" />
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { tools } from '@/utils/tools';
import useEditFields from '@/hooks/edit-field';
import useVirtualList from '@/hooks/virtual-list';
const { getRandomColor } = useEditFields();
import FhBoardCard from './components/fh-board-card/fh-board-card.vue';
import DashboardFieldSetting from '@/components/dashboard-field-setting/dashboard-field-setting.vue';
import FhListSkeleton from '@/components/fh-list/fh-list-skeleton/fh-list-skeleton.vue';
import { useI18n } from 'vue-i18n';
import groupConfigErrorSvg from '@/assets/images/group-config-error.svg';
import { cloneDeep } from 'lodash';

const { t } = useI18n();
const emits = defineEmits(['loadAdvancedComp', 'loadGroupData', 'getGroupTableData', 'loadMoreGroupData', 'refreshList', 'toDetails', 'getComputedStatus']);

const props = defineProps({
  viewId: {
    type: [Number, String],
    default: ''
  },
  entityId: {
    type: [Number, String],
    default: ''
  },
  height: {
    type: Number,
    default: 0
  },
  groupFieldInfo: {
    type: Object,
    default: () => {}
  },
  tableData: {
    type: Array,
    default: () => []
  },
  //是否隐藏看板label 1.隐藏 0.展示
  businessData: {
    type: String,
    default: '0'
  },
  // 新增按钮权限
  buttonUniversalAdd: {
    type: Object,
    default: () => {}
  },
  // 快捷按钮权限
  shortcutBtnPermission: {
    type: Object,
    default: () => ({})
  },
  fieldList: {
    type: Array,
    default: () => []
  },
  isPreview: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
});
// 计算属性
const _height = computed(() => props.height + 35);

// 虚拟列表相关配置
const itemWidth = 300; // 卡片组宽度
const bufferCount = 4; // 缓冲区数量
const throttleDelay = 300; // 节流延迟

const _businessData = ref(props.businessData);
const dashboardFieldRef = ref();
const allColumns = ref(props.fieldList);
watch(
  () => props.fieldList,
  () => {
    //如果是预览，会实时根据用户选择字段变更预览信息，监听字段列表，更新到看板中
    if (props.isPreview) {
      allColumns.value = props.fieldList;
    }
  }
);
const openDashboardFieldSettingModal = () => {
  dashboardFieldRef.value.open();
};
const refreshList = ({ fields = [], hideFieldName = false }) => {
  if (fields?.length) {
    allColumns.value = fields;
    _businessData.value = hideFieldName ? '1' : '0';
  }
  emits('refreshList');
};

// 初始化数据
const allGroups = computed(() => {
  const _computedData = [];
  props.tableData?.forEach((td) => {
    if (td?.groupKey) {
      _computedData.push({
        ...td,
        data: [],
        loadMore: null
      });
    } else if (!td?.uniqueMore) {
      //过滤加载更多选项
      const _findIndex = _computedData.findIndex((_cd) => _cd?.groupKey == td?.gk1);
      if (_findIndex > -1) {
        _computedData[_findIndex].data.push(cloneDeep(td));
      }
    } else if (td?.uniqueMore) {
      //loadMore选项卡
      const _findIndex = _computedData.findIndex((_cd) => _cd?.groupKey == td?.gk1);
      if (_findIndex > -1) {
        _computedData[_findIndex].loadMore = td;
      }
    }
  });

  return _computedData;
});

// 虚拟列表hook
const {
  scrollContainer,
  visibleItems,
  startOffset,
  totalWidth,
  handleScroll: virtualHandleScroll,
  initScrollPosition
} = useVirtualList({
  items: allGroups,
  itemWidth,
  bufferCount,
  throttleDelay
});

const toDetails = (item) => {
  emits('toDetails', item);
};

const getDefaultColor = (group, index) => {
  const _obj = props.groupFieldInfo || null;
  // 取后台设置的颜色
  if (_obj) {
    if (_obj?.fieldType == 6) {
      // 判断人员是否是空值
      if (!group?.value) return { background: '#d5d9dd', color: '#000' };
      return { background: getRandomColor(index), color: '#000' };
    }
    let _Array = [];
    if (tools.isJSON(_obj?.fieldExt)) {
      _Array = JSON.parse(_obj?.fieldExt);
      if (_Array.length > 0) {
        const _color = _Array.find((i) => i.label == group.name) || {};
        return { background: _color?.background || '#d5d9dd', color: _color?.color || '#000' };
      }
    }
  }
  return { background: '#d5d9dd', color: '#000' };
};

// 计算字段回调
const getComputedStatus = (computedInfo) => {
  emits('getComputedStatus', computedInfo);
};

const handleScrollBottom = (group) => {
  if (group?.loadMore) {
    emits('loadMoreGroupData', group.loadMore);
  }
};

// 可见组计算
const visibleGroups = computed(() => visibleItems.value);

watch(
  () => visibleGroups.value,
  () => {
    if (visibleGroups.value?.length) {
      //分组数据个数大于0并且数据未请求过【无权限字段】
      const _groupKeys = visibleGroups.value?.filter((vg) => vg?.count > 0 && vg?.data?.[0]?.LOGIC_ROLE_PERMISSION === undefined)?.map((vg) => vg?.groupKey);
      if (_groupKeys?.length) getTableDataByIds(_groupKeys);
    }
  },
  { deep: true }
);

// 自定义滚动处理函数
const handleScroll = tools.throttle((event) => {
  // 调用虚拟列表的滚动处理函数
  virtualHandleScroll(event);
}, throttleDelay);

onMounted(async () => {
  emits('loadAdvancedComp', true);
  // 初始渲染后确保位置正确
  nextTick(() => {
    initScrollPosition();
  });
});
const getTableDataByIds = async (groupKeys) => {
  try {
    props.tableData?.forEach((td) => {
      if (td?.groupKey && groupKeys?.includes(td?.groupKey)) {
        emits('loadGroupData', {
          dataIds: td?.dataIds,
          groupKey: td?.groupKey,
          level: td?.level
        });
      }
    });
  } catch (e) {
    console.log(e);
  }
};

defineExpose({ openDashboardFieldSettingModal });
</script>
<style scoped lang="less">
@import './fh-board-view.less';
</style>
